import React, { useState, useEffect } from "react";
import {
  Rating,
  alpha,
  SvgIcon,
  Tooltip,
  Typography,
  Box,
  useTheme,
} from "@mui/material"; // Added Tooltip, Typography, Box, useTheme
import { styled } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import GlassTileLayout from "../layouts/GlassTileLayout"; // Adjust path as needed

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconFilled": { color: "#ffd700" },
  "& .MuiRating-iconEmpty": {
    // Adjusted empty icon color for better visibility
    color: theme.palette.grey[400],
  },
}));

const AvgDiveRatingTile = ({ avgDiveRating, divesiteId }) => {
  const [userAverageRating, setUserAverageRating] = useState(null);
  const { userObjectId, currentUser } = useAuth();
  const [loadingUserDives, setLoadingUserDives] = useState(false);
  const [errorUserDives, setErrorUserDives] = useState(null);
  const theme = useTheme(); // Use theme for potential styling in tooltip

  useEffect(() => {
    const fetchUserAverageRating = async () => {
      // Only fetch if userObjectId is available and not already loading
      if (userObjectId && divesiteId && currentUser && !loadingUserDives) {
        setLoadingUserDives(true);
        setErrorUserDives(null);
        try {
          const token = await currentUser.getIdToken();
          const response = await axios.get(
            `/api/dives/user/${userObjectId}/divesite/${divesiteId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const dives = response.data;
          // Filter for dives at this specific site that have a diveRating
          const filteredDives = dives.filter(
            (dive) =>
              (dive.divesite === divesiteId ||
                dive.divesite === divesiteId.toString()) &&
              dive.diveRating !== null &&
              dive.diveRating !== undefined
          );

          if (filteredDives && filteredDives.length > 0) {
            const totalRating = filteredDives.reduce(
              (sum, dive) => sum + dive.diveRating,
              0
            );
            const average = totalRating / filteredDives.length;
            setUserAverageRating(average.toFixed(1)); // Rounded to one decimal place for display
          } else {
            setUserAverageRating("N/A");
          }
        } catch (error) {
          console.error("Error fetching user dives at this site:", error);
          setErrorUserDives(error);
          setUserAverageRating("Error"); // Indicate error in UI
        } finally {
          setLoadingUserDives(false);
        }
      } else if (!userObjectId) {
        // If no userObjectId, ensure state is reset for unauthenticated view
        setUserAverageRating("N/A");
      }
    };

    fetchUserAverageRating();
  }, [userObjectId, divesiteId, currentUser]); // Dependencies for useEffect

  // Function to generate content for the tooltip
  const getTooltipContent = () => {
    const overallRating =
      typeof avgDiveRating === "number" ? avgDiveRating.toFixed(1) : "N/A";
    const userRating = userAverageRating; // Already formatted or "N/A"

    return (
      <React.Fragment>
        <Typography
          color="inherit"
          variant="subtitle2"
          sx={{ fontWeight: 600 }}
        >
          Average Dive Rating
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          This is the average rating given by all divers for this site.
        </Typography>

        {/* Display user's average rating if available */}
        {userObjectId && ( // Only show this section if a user is logged in
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Your average: {userRating}
            {loadingUserDives && " (loading...)"}
            {errorUserDives && " (error)"}
          </Typography>
        )}

        {/* Display latest overall rating */}
        <Typography
          variant="caption"
          display="block"
          sx={{ mt: 0.5, fontStyle: "italic" }}
        >
          Overall: {overallRating}
        </Typography>
      </React.Fragment>
    );
  };

  return (
    <Tooltip
      title={getTooltipContent()}
      placement="top"
      arrow
      enterDelay={0} // Instant appearance on hover
      leaveDelay={100}
      enterTouchDelay={0} // Instant appearance on tap
    >
      {/* Box wrapper ensures the Tooltip has a direct, interactive DOM element */}
      <Box
        tabIndex={0}
        role="button"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          cursor: "pointer",
        }}
      >
        <GlassTileLayout
          icon={StarIcon} // Pass the icon component directly
          label="Avg Rating"
          value={
            typeof avgDiveRating === "number" ? avgDiveRating.toFixed(1) : "N/A"
          } // Ensure value is formatted
          iconColor="#ffd700" // Pass custom color for the star icon
        >
          <StyledRating
            value={typeof avgDiveRating === "number" ? avgDiveRating : 0}
            precision={0.5}
            size="small"
            readOnly
          />
        </GlassTileLayout>
      </Box>
    </Tooltip>
  );
};

export default AvgDiveRatingTile;
